import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import ApiService from '../../common/ApiCalls';
import DataView from '../../v2/components/DataView';
import FullDetailsAccordion from '../../v2/components/FullDetailsAccordion';
import { formatEntityAddress } from '../../v2/components/ExecutiveEntityQuickView';
import { updateValues } from '../../v2/services/api/update-fields.api';
import ExecutiveEntityLocations from './EntityLocations';
import HistoryDateControl, {
  browserTimezone,
  calendarToday
} from '../dynamictabs/HistoryDateControl';
import HistoricalLayout from '../dynamictabs/HistoricalLayout';
import './EntityDetail.scss';

const getCount = (stats, type) =>
  Number(stats?.find((stat) => stat.type === type)?.count) || 0;

const normalizeFieldValue = (fieldType, value) => {
  if (fieldType === 'multiselect') {
    if (Array.isArray(value)) return value;
    return typeof value === 'string' && value ? value.split(',') : [];
  }

  return value ?? '';
};

const buildFormValues = (layout, fieldValues) => {
  const savedValues = Object.fromEntries(
    (fieldValues || []).map((value) => [value.field_id, value.value])
  );

  return Object.fromEntries(
    (layout?.cards || [])
      .flatMap((card) => card.items || [])
      .map((item) => [
        item.field_id,
        normalizeFieldValue(item.field?.field_type, savedValues[item.field_id])
      ])
  );
};

const serializeFieldValue = (fieldType, value) => {
  if (Array.isArray(value)) return value.join(',');
  if (fieldType === 'checkbox') return String(value) === 'true';
  return value;
};

const ExecutiveEntityDetailContent = ({ entityId }) => {
  const navigate = useNavigate();
  const [timezone] = useState(browserTimezone);
  const today = calendarToday(timezone);
  const [date, setDate] = useState(today);
  const historyRevision = 0;
  const historical = date !== today;
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [draftValues, setDraftValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveAlert, setSaveAlert] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const loadDetail = async () => {
    setLoading(true);
    setError(false);
    try {
      const detail = await ApiService().getExecutiveEntityDetail(entityId);
      setResponse(detail);
      setDraftValues(buildFormValues(detail?.layout, detail?.values));
      setIsEditing(false);
      setActiveTab(0);
      setExpandedCards(
        Object.fromEntries(
          (detail?.layout?.cards || []).map((card, index) => [
            `${card.title}-${index}`,
            true
          ])
        )
      );
    } catch (requestError) {
      console.error('Executive entity detail failed', requestError);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const values = useMemo(
    () => buildFormValues(response?.layout, response?.values),
    [response?.layout, response?.values]
  );

  const beginEditing = () => {
    setDraftValues(values);
    setIsEditing(true);
    setSaveAlert(null);
  };

  const cancelEditing = () => {
    setDraftValues(values);
    setIsEditing(false);
  };

  const handleFieldChange = ({ target }) => {
    setDraftValues((previous) => ({
      ...previous,
      [target.name]: target.value
    }));
  };

  const saveChanges = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaveAlert(null);

    const fieldTypes = Object.fromEntries(
      (response?.layout?.cards || [])
        .flatMap((card) => card.items || [])
        .map((item) => [item.field_id, item.field?.field_type])
    );
    const updatedValues = Object.entries(draftValues).map(
      ([fieldId, value]) => ({
        field_id: fieldId,
        subject_type: 'entity',
        object_type: 'entity',
        subject_id: entityId,
        object_id: null,
        value: serializeFieldValue(fieldTypes[fieldId], value)
      })
    );

    try {
      await updateValues({ values: updatedValues });
      setResponse((previous) => ({
        ...previous,
        values: updatedValues.map((value) => ({
          field_id: value.field_id,
          value: value.value
        }))
      }));
      setIsEditing(false);
      setSaveAlert({ severity: 'success', message: 'Entity details saved.' });
    } catch (saveError) {
      console.error('Executive entity detail save failed', saveError);
      setSaveAlert({
        severity: 'error',
        message: 'Entity details could not be saved. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="executive-entity-detail__state">
        <CircularProgress />
        <Typography>Loading entity detail...</Typography>
      </Box>
    );
  }

  if (error || !response?.entity) {
    return (
      <Box className="executive-entity-detail__state">
        <Alert severity="error">Entity detail could not be loaded.</Alert>
        <Button variant="contained" onClick={loadDetail}>
          Retry
        </Button>
        <Button onClick={() => navigate('/home')}>Back to map</Button>
      </Box>
    );
  }

  const { entity, layout, locations = [] } = response;
  const entityPermissions =
    entity.__permissions__ || entity['**permissions**'] || [];
  const canEdit = entityPermissions.includes('entities.update');
  const addressRecord = entity.addresses?.[0];
  const address = formatEntityAddress(addressRecord);
  const coordinates =
    addressRecord?.lat != null && addressRecord?.long != null
      ? `${addressRecord.lat},${addressRecord.long}`
      : null;
  const metrics = [
    { label: 'Locations', value: getCount(entity.stats, 'assets') },
    { label: 'Services', value: entity.service_ids?.length || 0 },
    { label: 'Accounts', value: getCount(entity.stats, 'contracts') }
  ];

  return (
    <Box className="executive-entity-detail">
      <Paper className="executive-entity-detail__header" elevation={1}>
        <IconButton aria-label="back to map" onClick={() => navigate('/home')}>
          <ArrowBack />
        </IconButton>
        <BusinessOutlined className="executive-entity-detail__icon" />
        <Box className="executive-entity-detail__identity">
          <Typography className="executive-entity-detail__eyebrow">
            Executive view
          </Typography>
          <Typography variant="h4">{entity.name}</Typography>
          <Box className="executive-entity-detail__contact">
            {coordinates ? (
              <Link
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coordinates)}`}
                target="_blank"
                rel="noreferrer"
                underline="hover"
              >
                <PlaceOutlined /> {address}
              </Link>
            ) : (
              <span>
                <PlaceOutlined /> {address}
              </span>
            )}
            {entity.phone && (
              <Link href={`tel:${entity.phone}`} underline="hover">
                <PhoneOutlined /> {entity.phone}
              </Link>
            )}
          </Box>
        </Box>
        <Box className="executive-entity-detail__metrics">
          {metrics.map((metric) => (
            <Box key={metric.label} className="executive-entity-detail__metric">
              <Typography variant="h5">{metric.value}</Typography>
              <Typography>{metric.label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      <Box className="executive-entity-detail__tabs">
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          aria-label="Executive entity details"
        >
          <Tab label="Overview" />
          <Tab label={`Locations (${locations.length})`} />
        </Tabs>
      </Box>

      {activeTab === 0 ? (
        <Box
          className="executive-entity-detail__layout"
          component="form"
          onSubmit={saveChanges}
        >
          <Box className="executive-entity-detail__layout-heading">
            <Box className="executive-entity-detail__layout-heading-copy">
              <Typography variant="h5">
                {layout?.name || 'Entity details'}
              </Typography>
              {layout?.description && (
                <Typography>{layout.description}</Typography>
              )}
            </Box>
            {!historical && canEdit && layout?.cards?.length ? (
              <Box className="executive-entity-detail__actions">
                {isEditing ? (
                  <>
                    <Button
                      type="button"
                      onClick={cancelEditing}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<EditOutlined />}
                    onClick={beginEditing}
                  >
                    Edit details
                  </Button>
                )}
              </Box>
            ) : null}
          </Box>

          {!saving && !isEditing && (
            <>
              <HistoryDateControl
                date={date}
                today={today}
                timezone={timezone}
                onChange={(nextDate) => {
                  setDate(nextDate);
                  if (nextDate === today) loadDetail();
                }}
              />

            </>
          )}
          {historical ? (
            <HistoricalLayout
              key={`${entityId}:${historyRevision}`}
              id={entityId}
              subjectType="entity"
              objectType="entity"
              date={date}
              timezone={timezone}
              canEditSubject={canEdit}
            />
          ) : layout?.cards?.length ? (
            layout.cards.map((card, index) => {
              const key = `${card.title}-${index}`;
              return (
                <FullDetailsAccordion
                  key={key}
                  id={key}
                  expanded={Boolean(expandedCards[key])}
                  selectCallback={(selected) =>
                    setExpandedCards((previous) => ({
                      ...previous,
                      [key]: Boolean(selected)
                    }))
                  }
                  detailsComponent={
                    <DataView
                      orientation="horizontal"
                      card={card}
                      isEdit={isEditing}
                      values={isEditing ? draftValues : values}
                      handleChange={handleFieldChange}
                    />
                  }
                >
                  <span className="title">{card.title}</span>
                </FullDetailsAccordion>
              );
            })
          ) : (
            <Paper
              className="executive-entity-detail__empty"
              variant="outlined"
            >
              <Typography variant="h6">
                No entity layout cards configured
              </Typography>
              <Typography>
                An administrator can configure this view in Admin &gt; Layouts
                &gt; Entity.
              </Typography>
            </Paper>
          )}
        </Box>
      ) : (
        <ExecutiveEntityLocations locations={locations} />
      )}

      <Snackbar
        open={Boolean(saveAlert)}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSaveAlert(null)}
      >
        <Alert
          severity={saveAlert?.severity || 'success'}
          variant="filled"
          onClose={() => setSaveAlert(null)}
        >
          {saveAlert?.message || ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const ExecutiveEntityDetail = () => {
  const { entityId } = useParams();
  return <ExecutiveEntityDetailContent key={entityId} entityId={entityId} />;
};

export default ExecutiveEntityDetail;
